import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Loader2 } from "lucide-react";
import "@/App.css";

// Lazy-load routes for performance (great for SEO / LCP)
const Home = lazy(() => import("@/pages/Home"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const LegalNoticeGen = lazy(() => import("@/pages/tools/LegalNoticeGenerator"));
const ComplaintLetterGen = lazy(() => import("@/pages/tools/ComplaintLetterGenerator"));
const CaseChecker = lazy(() => import("@/pages/tools/CaseChecker"));
const CompensationCalc = lazy(() => import("@/pages/tools/CompensationCalculator"));
const Companies = lazy(() => import("@/pages/Companies"));
const ComplaintAgainstCompany = lazy(() => import("@/pages/ComplaintAgainstCompany"));
const Forums = lazy(() => import("@/pages/Forums"));
const ForumsByCity = lazy(() => import("@/pages/ForumsByCity"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Faq = lazy(() => import("@/pages/Faq"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));
const Terms = lazy(() => import("@/pages/legal/Terms"));
const Disclaimer = lazy(() => import("@/pages/legal/Disclaimer"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));

const Fallback = () => (
    <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-[var(--np-text-muted)]" />
    </div>
);

function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Layout>
                        <Suspense fallback={<Fallback />}>
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/register" element={<Register />} />

                                <Route path="/tools/legal-notice-generator" element={<LegalNoticeGen />} />
                                <Route path="/tools/complaint-letter-generator" element={<ComplaintLetterGen />} />
                                <Route path="/tools/case-checker" element={<CaseChecker />} />
                                <Route path="/tools/compensation-calculator" element={<CompensationCalc />} />

                                <Route path="/companies" element={<Companies />} />
                                <Route path="/complaint-against/:slug" element={<ComplaintAgainstCompany />} />

                                <Route path="/consumer-forums" element={<Forums />} />
                                <Route path="/consumer-forums/:city" element={<ForumsByCity />} />

                                <Route path="/blog" element={<Blog />} />
                                <Route path="/blog/:slug" element={<BlogPost />} />

                                <Route path="/faq" element={<Faq />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/privacy" element={<Privacy />} />
                                <Route path="/terms" element={<Terms />} />
                                <Route path="/disclaimer" element={<Disclaimer />} />

                                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                                <Route path="/admin/*" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />

                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </Suspense>
                    </Layout>
                    <Suspense fallback={null}>
                        <CookieConsent />
                    </Suspense>
                    <Toaster position="top-right" richColors />
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;
